import React, { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Alert, Button, ButtonGroup, Col, Dropdown, Form, InputGroup, Row, Table } from 'react-bootstrap'
import { useAdminAnalyticsData } from 'api/hooks/use-admin-analytics-data'
import { useSyncAdminConfigKeysFromCode } from 'api/hooks/use-admin-analytic-configs'
import AppBadge from 'shared/badges/app-badge'
import { ActionButton } from 'shared/buttons'
import TableLoading from 'shared/tables/table-loading'
import TableError from 'shared/tables/table-error'
import SortArrow from 'shared/tables/sort-arrow'
import { getColumnOrder } from 'shared/tables/utils'
import ConfigSyncHistoryModal from './ConfigSyncHistoryModal'
import CreateCustomAnalyticModal from './CreateCustomAnalyticModal'
import {
  TYPE_ALL,
  TYPE_CUSTOM,
  TYPE_DEFAULT,
  customMunicipalities,
  filterAnalytics,
  filtersFromSearchParams,
  searchParamsFromFilters,
  sortAnalytics
} from './filters'

const COLUMNS = 8

const TYPE_OPTIONS = [
  { value: TYPE_DEFAULT, label: 'Default' },
  { value: TYPE_CUSTOM, label: 'Custom' },
  { value: TYPE_ALL, label: 'All' }
]

export const analyticPath = (analytic) => `/admin/analytics-data/${analytic.id}`

const TypeCell = ({ analytic }) => {
  if (analytic.custom) {
    return (
      <>
        <AppBadge variant="ai">Custom</AppBadge>
        <div className="small text-muted">{analytic.municipality_name}</div>
      </>
    )
  }
  return analytic.is_core ? (
    <AppBadge variant="primary">Default</AppBadge>
  ) : (
    <AppBadge className="bg-light text-primary border">Default · Off</AppBadge>
  )
}

const SortableHeader = ({ column, filters, onSort, children }) => (
  <th role="button" onClick={() => onSort(column)}>
    {children}
    <SortArrow column={column} orderBy={filters.orderBy} orderDirection={filters.orderDirection} />
  </th>
)

const syncSummary = ({ created_keys: created, attached, missing_definitions: missing }) => {
  const parts = [`${attached.length} key${attached.length === 1 ? '' : 's'} attached`]
  if (created.length > 0) parts.push(`${created.length} defined from the code`)
  if (missing.length > 0) {
    parts.push(`${missing.map((m) => m.config_key).join(', ')} need a definition the code can't give`)
  }
  return `Synced from the analytics code: ${parts.join('; ')}.`
}

const AnalyticsDataList = () => {
  const { data: analytics, isLoading, error } = useAdminAnalyticsData()
  const [searchParams, setSearchParams] = useSearchParams()
  const [showCustomModal, setShowCustomModal] = useState(false)
  const [syncNotice, setSyncNotice] = useState(null)
  const [history, setHistory] = useState({ show: false, runId: null })
  const syncMutation = useSyncAdminConfigKeysFromCode({
    options: { onSuccess: (run) => setSyncNotice({ text: syncSummary(run.report), runId: run.id }) }
  })
  const filters = filtersFromSearchParams(searchParams)

  const setFilters = (changes) =>
    setSearchParams(searchParamsFromFilters({ ...filters, ...changes }), { replace: true })

  const handleSort = (column) =>
    setFilters({ orderBy: column, orderDirection: getColumnOrder(filters.orderBy === column, filters) })

  const sections = useMemo(() => {
    const bySection = new Map()
    analytics?.forEach((a) => a.section_abbreviation && bySection.set(a.section_abbreviation, a.section_name))
    return [...bySection].sort((a, b) => a[1].localeCompare(b[1]))
  }, [analytics])

  const municipalities = useMemo(() => customMunicipalities(analytics), [analytics])

  const visible = useMemo(() => {
    const current = filtersFromSearchParams(searchParams)
    return sortAnalytics(filterAnalytics(analytics, current), current)
  }, [analytics, searchParams])

  const sortProps = { filters, onSort: handleSort }

  const renderBody = () => {
    if (isLoading) return <TableLoading columns={COLUMNS} />
    if (error) return <TableError columns={COLUMNS} />
    if (visible.length === 0) {
      return (
        <tr>
          <td colSpan={COLUMNS} className="text-center text-muted py-4">
            No analytics match these filters.
          </td>
        </tr>
      )
    }

    return visible.map((analytic) => (
      <tr key={analytic.id} data-testid="analytic-row">
        <td>
          <Link to={analyticPath(analytic)}>
            <code>{analytic.key}</code>
          </Link>
        </td>
        <td>{analytic.name}</td>
        <td>{analytic.section_abbreviation ? <AppBadge variant="info">{analytic.section_abbreviation}</AppBadge> : '-'}</td>
        <td>
          <TypeCell analytic={analytic} />
        </td>
        <td className="text-end">{analytic.municipality_count}</td>
        <td className="text-end">
          {analytic.flags_count.toLocaleString()}
          <div className="small text-muted">
            {analytic.flagging_municipality_count} of {analytic.municipality_count} flagging
          </div>
        </td>
        <td className="text-end">
          {analytic.error_count > 0 ? (
            <AppBadge variant="danger">{analytic.error_count}</AppBadge>
          ) : (
            <span className="text-muted">0</span>
          )}
        </td>
        <td style={{ whiteSpace: 'nowrap' }}>
          <Button as={Link} to={analyticPath(analytic)} variant="light" size="sm" className="text-primary">
            View
          </Button>
        </td>
      </tr>
    ))
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h2 mb-0">Analytics</h1>
        <div className="d-flex gap-2">
          <Button variant="light" className="text-primary" onClick={() => setHistory({ show: true, runId: null })}>
            See Sync History
          </Button>
          <ActionButton
            label="Sync All Configs From Code"
            variant="light"
            size="md"
            className="text-primary"
            title="Attach the config keys the deployed analytics code reads to every analytic"
            onClick={() => {
              setSyncNotice(null)
              syncMutation.mutate()
            }}
            loading={syncMutation.isLoading}
            loadingLabel="Syncing"
          />
          <Dropdown align="end">
            <Dropdown.Toggle variant="primary">Create Analytic</Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item as={Link} to="/admin/analytics-data/new">
                Default analytic
              </Dropdown.Item>
              <Dropdown.Item as="button" onClick={() => setShowCustomModal(true)}>
                Custom analytic for a municipality…
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      {syncMutation.error && (
        <Alert variant="danger" className="small" data-testid="code-sync-result">
          {syncMutation.error?.response?.data?.message || syncMutation.error?.message}
        </Alert>
      )}
      {syncNotice && (
        <Alert variant="success" className="small" data-testid="code-sync-result">
          {syncNotice.text}{' '}
          <Button
            variant="link"
            size="sm"
            className="p-0 align-baseline"
            onClick={() => setHistory({ show: true, runId: syncNotice.runId })}
          >
            View report
          </Button>
        </Alert>
      )}

      <Row className="mb-3 g-3 align-items-end">
        <Col md="auto">
          <Form.Label className="small text-muted mb-1 d-block">Type</Form.Label>
          <ButtonGroup aria-label="Analytic type">
            {TYPE_OPTIONS.map(({ value, label }) => (
              <Button
                key={value}
                variant={filters.type === value ? 'primary' : 'light'}
                className={filters.type === value ? '' : 'text-primary'}
                onClick={() => setFilters({ type: value, municipality: value === TYPE_DEFAULT ? '' : filters.municipality })}
              >
                {label}
              </Button>
            ))}
          </ButtonGroup>
        </Col>
        <Col md>
          <InputGroup>
            <InputGroup.Text>Search</InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Key, name, description, module or municipality..."
              value={filters.search}
              onChange={(e) => setFilters({ search: e.target.value })}
            />
          </InputGroup>
        </Col>
        <Col md={2}>
          <Form.Label className="small text-muted mb-1">Module</Form.Label>
          <Form.Select aria-label="Module" value={filters.section} onChange={(e) => setFilters({ section: e.target.value })}>
            <option value="">All modules</option>
            {sections.map(([abbreviation, name]) => (
              <option key={abbreviation} value={abbreviation}>
                {name}
              </option>
            ))}
          </Form.Select>
        </Col>
        {filters.type !== TYPE_DEFAULT && (
          <Col md={2}>
            <Form.Label className="small text-muted mb-1">Municipality</Form.Label>
            <Form.Select
              aria-label="Municipality"
              value={filters.municipality}
              onChange={(e) => setFilters({ municipality: e.target.value })}
            >
              <option value="">All municipalities</option>
              {municipalities.map(({ id, name }) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </Form.Select>
          </Col>
        )}
      </Row>
      <Row className="mb-3">
        <Col className="d-flex gap-4">
          {filters.type === TYPE_DEFAULT && (
            <Form.Check
              type="switch"
              id="include-inactive"
              label="Include analytics that are off (not core)"
              checked={filters.includeInactive}
              onChange={(e) => setFilters({ includeInactive: e.target.checked })}
            />
          )}
          <Form.Check
            type="switch"
            id="errors-only"
            label="Only analytics with errors"
            checked={filters.errorsOnly}
            onChange={(e) => setFilters({ errorsOnly: e.target.checked })}
          />
        </Col>
      </Row>

      {error && <Alert variant="danger">Error loading analytics: {error.message}</Alert>}

      {!isLoading && !error && (
        <div className="mb-2 text-muted" data-testid="analytics-count">
          Showing {visible.length} of {analytics?.length || 0} analytics
        </div>
      )}

      <div className="table-responsive">
        <Table bordered hover>
          <thead>
            <tr>
              <SortableHeader column="key" {...sortProps}>Key</SortableHeader>
              <SortableHeader column="name" {...sortProps}>Name</SortableHeader>
              <th>Module</th>
              <th>Type</th>
              <SortableHeader column="municipality_count" {...sortProps}>Municipalities</SortableHeader>
              <SortableHeader column="flags_count" {...sortProps}>Open Flags</SortableHeader>
              <SortableHeader column="error_count" {...sortProps}>Errors</SortableHeader>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>{renderBody()}</tbody>
        </Table>
      </div>

      <CreateCustomAnalyticModal show={showCustomModal} onHide={() => setShowCustomModal(false)} />
      <ConfigSyncHistoryModal
        show={history.show}
        initialRunId={history.runId}
        onHide={() => setHistory({ show: false, runId: null })}
      />
    </div>
  )
}

export default AnalyticsDataList
